import { FC, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box, DialogTitle, Link } from '@mui/material';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsDialog } from '../common/basic/QcsDialog';
import { QcsDialogActions } from '../common/basic/QcsDialogActions';
import { QcsDialogContentText } from '../common/basic/QcsDialogContentText';
import { uniqueBy } from '../../utils/array';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { SubcontractorRequestDto } from '@api/models/suppliers/SubcontractorRequestDto';

interface Props {
  open: boolean;
  onClose: () => void;
  requests: SubcontractorRequestDto[];
}

// Informs a company user, when opening an existing supplier's card, that another
// supplier has requested this supplier to activate "canLendEmployees" for them.
export const SubcontractorLendRequestDialog: FC<Props> = ({ open, onClose, requests }) => {
  const { t } = useTranslation();
  const { getPath } = useNavigation();

  // Multiple requests can come from the same requesting supplier (one per requested
  // employee) — de-duplicate since this dialog is only about the activation request itself.
  const requestingSuppliers = uniqueBy(
    requests.map((request) => request.requestingSupplier),
    (supplier) => supplier?.id,
  );

  const isSingle = requestingSuppliers.length === 1;

  const supplierLinks = requestingSuppliers.map((supplier, index) => (
    <Fragment key={supplier?.id ?? index}>
      {index > 0 && ', '}
      <Link component={RouterLink} to={getPath(PathKey.SupplierDetail, { supplierId: supplier?.id ?? '' })}>
        {supplier?.name}
      </Link>
    </Fragment>
  ));

  return (
    <QcsDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('supplier.subcontractorLendRequest.title')}</DialogTitle>
      <QcsDialogContentText sx={{ px: 1, py: 1, fontSize: '1rem', lineHeight: 1.7 }}>
        <Trans
          i18nKey={isSingle ? 'supplier.subcontractorLendRequest.single' : 'supplier.subcontractorLendRequest.multiple'}
          values={{ field: t('supplier.canLendEmployees') }}
          components={{ suppliers: <Box component="span">{supplierLinks}</Box> }}
        />
      </QcsDialogContentText>
      <QcsDialogActions>
        <QcsButton variant="contained" onClick={onClose}>
          {t('common.close')}
        </QcsButton>
      </QcsDialogActions>
    </QcsDialog>
  );
};
